import React, { useState, useRef, useEffect } from "react";
import { FaTachometerAlt, FaSearch, FaBalanceScale } from "react-icons/fa";
import { Table, Container, Row, Col, Spinner } from "react-bootstrap";
import CountryList from "../../CountryList";
import { googleApi } from "../../../services/api";

const KeywordDifficulty = () => {
  const [keyword, setKeyword] = useState("");
  const [country, setCountry] = useState("us");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");

  // Comparison
  const [keyword1, setKeyword1] = useState("");
  const [keyword2, setKeyword2] = useState("");
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState(null);
  const [compareStatusMsg, setCompareStatusMsg] = useState("");

  // timers, guards, aborts
  const searchTimers = useRef([]);
  const compareTimers = useRef([]);
  const searchReqId = useRef(0);
  const compareReqId = useRef(0);
  const searchAbortRef = useRef(null);
  const compareAbortRef = useRef(null);

  useEffect(() => {
    const st = searchTimers.current;
    const ct = compareTimers.current;
    const sa = searchAbortRef.current;
    const ca = compareAbortRef.current;
    return () => {
      st.forEach(clearTimeout);
      ct.forEach(clearTimeout);
      if (sa) sa.abort();
      if (ca) ca.abort();
    };
  }, []);

  const startTimedMessages = (setFn, bucketRef) => {
    bucketRef.current.forEach(clearTimeout);
    bucketRef.current = [];
    setFn("Fetching your results…");
    bucketRef.current.push(setTimeout(() => setFn("Still fetching… this may take a little while."), 10_000));
    bucketRef.current.push(setTimeout(() => setFn("Working on it… analyzing many results can be slow."), 30_000));
    bucketRef.current.push(setTimeout(() => setFn("Almost there… large datasets take longer than usual."), 60_000));
  };
  const stopTimedMessages = (setFn, bucketRef) => {
    bucketRef.current.forEach(clearTimeout);
    bucketRef.current = [];
    setFn("");
  };

  const handleSearch = async () => {
    if (!keyword.trim()) {
      setError("Please enter a keyword.");
      return;
    }

    // abort any in-flight
    if (searchAbortRef.current) searchAbortRef.current.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;

    const myId = ++searchReqId.current;

    setLoading(true);
    setError(null);
    setData(null);
    startTimedMessages(setStatusMsg, searchTimers);

    try {
      const response = await googleApi.get("keyword-difficulty/", {
        params: { keyword: keyword.trim(), country },
        signal: controller.signal,
      });

      // only set data if this response is still the latest
      if (searchReqId.current === myId) {
        setData(response.data);
      }
    } catch (err) {
      const canceled =
        err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.message === "canceled";
      if (!canceled && searchReqId.current === myId) {
        setError("Failed to fetch data. Try again.");
      }
    } finally {
      // ALWAYS clear loader/messages for this interaction
      setLoading(false);
      stopTimedMessages(setStatusMsg, searchTimers);
      if (searchAbortRef.current === controller) searchAbortRef.current = null;
    }
  };

  const handleCompare = async () => {
    if (!keyword1.trim() || !keyword2.trim()) {
      setCompareError("Please enter both keywords.");
      return;
    }

    if (compareAbortRef.current) compareAbortRef.current.abort();
    const controller = new AbortController();
    compareAbortRef.current = controller;

    const myId = ++compareReqId.current;

    setCompareLoading(true);
    setCompareError(null);
    setCompareData(null);
    startTimedMessages(setCompareStatusMsg, compareTimers);

    try {
      const response = await googleApi.get("keyword-difficulty/", {
        params: { keyword: keyword1.trim(), keyword2: keyword2.trim(), country },
        signal: controller.signal,
      });

      if (compareReqId.current === myId) {
        setCompareData(response.data);
      }
    } catch (err) {
      const canceled =
        err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.message === "canceled";
      if (!canceled && compareReqId.current === myId) {
        setCompareError("Failed to fetch comparison. Try again.");
      }
    } finally {
      setCompareLoading(false);
      stopTimedMessages(setCompareStatusMsg, compareTimers);
      if (compareAbortRef.current === controller) compareAbortRef.current = null;
    }
  };

  return (
    <Container className="mt-4">
      {/* ---------- Single Keyword Section ---------- */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <FaTachometerAlt className="mr-3 text-red-500" />
          Keyword Difficulty Analysis
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-5">
                <label className="block text-sm font-medium text-gray-700 mb-1">Enter Keyword</label>
                <input
                    type="text"
                    placeholder="Enter keyword"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                />
            </div>
            <div className="md:col-span-5">
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Country</label>
                <CountryList onSelect={(selectedCountry) => setCountry(selectedCountry)} />
            </div>
            <div className="md:col-span-2">
                 <button
                    onClick={handleSearch}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md shadow transition duration-200 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                    disabled={loading}
                    >
                    {loading ? (
                        <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    ) : (
                        <FaSearch className="mr-2" />
                    )}
                    {loading ? "Searching..." : "Search"}
                 </button>
            </div>
        </div>
      </div>

      {error && <p className="text-danger text-center">{error}</p>}
      
      {/* ... (keeping loader and results logic) ... */}
      {loading && !data && (
        <div className="text-center">
          <Spinner animation="border" variant="dark" />
          {statusMsg && <div className="mt-2 text-muted" style={{ fontSize: 12 }}>{statusMsg}</div>}
        </div>
      )}

      {data && (
        <>
          {/* ... (rest of result display) ... */}
          <Row className="mb-4">
            <Col>
              <h2 className="fw-bold">Analysis Results</h2>
              <p style={{ fontSize: "14px", opacity: "0.8" }}>
                This shows how competitive the keyword <strong>{data.keyword}</strong> is in{" "}
                <strong>{data.country.toUpperCase()}</strong>.
              </p>
              <h5 className="mt-3">
                Overall Difficulty Score:{" "}
                <span style={{ color: "#0e1536ff", fontWeight: "bold" }}>
                  {data.difficulty_score}
                </span>{" "}
                / 100
              </h5>
            </Col>
          </Row>

          <Row>
            <Col>
              <Table
                bordered
                hover
                responsive
                striped
                style={{ backgroundColor: "white", color: "#001f3f", fontSize: "0.8rem" }}
              >
                <thead style={{ backgroundColor: "#001f3f", color: "white" }}>
                  <tr>
                    <th>#</th>
                    <th>App Name</th>
                    <th>Installs</th>
                    <th>Rating</th>
                    <th>Reviews</th>
                    <th>Difficulty Contribution</th>
                  </tr>
                </thead>
                <tbody>
                  {data.apps_analyzed.map((app, index) => (
                    <tr key={app.package_name}>
                      <td>{index + 1}</td>
                      <td><strong>{app.app_name}</strong></td>
                      <td>{app.installs}</td>
                      <td>{app.rating.toFixed(2)}</td>
                      <td>{app.reviews.toLocaleString()}</td>
                      <td>
                        {app.difficulty_contribution}%<br />
                        <small style={{ color: "#666" }}>(Higher = Stronger Competitor)</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          </Row>
        </>
      )}

      {/* ---------- Comparison Section ---------- */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100 mt-8">
         <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <FaBalanceScale className="mr-3 text-green-500" />
          Keyword Comparison
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Keyword 1</label>
                <input
                    type="text"
                    placeholder="Keyword 1"
                    value={keyword1}
                    onChange={(e) => setKeyword1(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition duration-200"
                />
            </div>
            <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Keyword 2</label>
                <input
                    type="text"
                    placeholder="Keyword 2"
                    value={keyword2}
                    onChange={(e) => setKeyword2(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition duration-200"
                />
            </div>
            <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Country</label>
                <CountryList onSelect={(selectedCountry) => setCountry(selectedCountry)} />
            </div>
            <div className="md:col-span-3">
                 <button
                    onClick={handleCompare}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md shadow transition duration-200 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                    disabled={compareLoading}
                    >
                    {compareLoading ? (
                        <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    ) : (
                        <FaBalanceScale className="mr-2" />
                    )}
                    {compareLoading ? "Comparing..." : "Compare"}
                 </button>
            </div>
        </div>
      </div>

      {compareError && <p className="text-danger text-center">{compareError}</p>}

      {compareLoading && !compareData && (
        <div className="text-center">
          <Spinner animation="border" variant="dark" />
          {compareStatusMsg && <div className="mt-2 text-muted" style={{ fontSize: 12 }}>{compareStatusMsg}</div>}
        </div>
      )}

      {compareData?.comparison && (
        <Row>
          <Col>
            {Object.entries(compareData.comparison).map(([key, value], idx) => (
              <div key={idx} className="mb-5">
                <h4 className="fw-bold">
                  {key} → Difficulty Score:{" "}
                  <span style={{ color: value.difficulty_score > 80 ? "red" : "green" }}>
                    {value.difficulty_score}
                  </span>{" "}
                  / 100
                </h4>
                <Table
                  className="mt-4"
                  bordered
                  hover
                  responsive
                  striped
                  style={{ backgroundColor: "white", color: "#001f3f", fontSize: "0.8rem" }}
                >
                  <thead style={{ backgroundColor: "#001f3f", color: "white" }}>
                    <tr>
                      <th>#</th>
                      <th>App Name</th>
                      <th>Installs</th>
                      <th>Rating</th>
                      <th>Reviews</th>
                      <th>Difficulty Contribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {value.apps_analyzed.map((app, i) => (
                      <tr key={app.package_name}>
                        <td>{i + 1}</td>
                        <td><strong>{app.app_name}</strong></td>
                        <td>{app.installs}</td>
                        <td>{app.rating.toFixed(2)}</td>
                        <td>{app.reviews.toLocaleString()}</td>
                        <td>{app.difficulty_contribution}%</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ))}
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default KeywordDifficulty;
